import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Building2, Users, GraduationCap, DollarSign, Plus, Copy, ExternalLink,
  Upload, Image, CreditCard, CheckCircle, XCircle, Clock, AlertCircle,
  MoreVertical, Key, Ban, MessageCircle, Calendar, Eye, RefreshCw, Edit, Save
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import TenantResetManager from '@/components/TenantResetManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);
  const [stats, setStats] = useState({
    schools: 0,
    students: 0,
    teachers: 0,
    revenue: 0
  });
  const [schools, setSchools] = useState([]);
  const [plans, setPlans] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  
  // School Management States
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolDetailDialog, setSchoolDetailDialog] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [editSchoolDialog, setEditSchoolDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [schoolAdmins, setSchoolAdmins] = useState([]);
  
  // Edit School Form
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    current_session: '',
    plan_id: '',
    subscription_end_date: ''
  });
  
  const [formData, setFormData] = useState({
    school_code: '',
    school_name: '',
    school_email: '',
    school_phone: '',
    school_address: '',
    current_session: '2026',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    plan_id: '',
    billing_cycle: 'monthly'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schoolsRes, plansRes, subsRes] = await Promise.all([
        axios.get(`${API_URL}/api/schools`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/subscriptions/plans`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/subscriptions/schools`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      
      // Merge subscription data with schools
      const schoolsWithSubs = schoolsRes.data.map(school => {
        const sub = subsRes.data.find(s => s.school_id === school.id);
        return { ...school, subscription: sub };
      });
      
      setSchools(schoolsWithSubs);
      setPlans(plansRes.data.filter(p => p.is_active));
      
      // Calculate stats
      const totalStudents = schoolsWithSubs.reduce((sum, s) => sum + (s.student_count || 0), 0);
      const totalTeachers = schoolsWithSubs.reduce((sum, s) => sum + (s.teacher_count || 0), 0);
      const activeSubs = subsRes.data.filter(s => s.status === 'active').length;
      
      setStats({
        schools: schoolsRes.data.length,
        students: totalStudents,
        teachers: totalTeachers,
        revenue: activeSubs * 999 // Placeholder
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo must be less than 2MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Use FormData for multipart upload
      const submitData = new FormData();
      submitData.append('school_code', formData.school_code.toUpperCase());
      submitData.append('name', formData.school_name);
      submitData.append('email', formData.school_email);
      submitData.append('phone', formData.school_phone);
      submitData.append('address', formData.school_address);
      // Auto-generate current_session based on current year
      const currentYear = new Date().getFullYear();
      submitData.append('current_session', `${currentYear}-${(currentYear + 1).toString().slice(-2)}`);
      submitData.append('admin_name', formData.admin_name);
      submitData.append('admin_email', formData.admin_email);
      submitData.append('admin_password', formData.admin_password);
      
      if (formData.plan_id) {
        submitData.append('plan_id', formData.plan_id);
        submitData.append('billing_cycle', formData.billing_cycle);
      }
      
      if (logoFile) {
        submitData.append('logo', logoFile);
      }
      
      await axios.post(`${API_URL}/api/schools/with-subscription`, submitData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('School created successfully!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create school');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      school_code: '',
      school_name: '',
      school_email: '',
      school_phone: '',
      school_address: '',
      current_session: '2026',
      admin_name: '',
      admin_email: '',
      admin_password: '',
      plan_id: '',
      billing_cycle: 'monthly'
    });
    setLogoPreview(null);
    setLogoFile(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied!');
  };

  const getSchoolPortalUrl = (schoolCode) => {
    return `${window.location.origin}/${schoolCode.toLowerCase()}`;
  };

  // Toggle school active/block status
  const toggleSchoolStatus = async (school) => {
    try {
      await axios.patch(`${API_URL}/api/schools/${school.id}/toggle-active`, {}, {
        headers: getAuthHeader()
      });
      toast.success(school.is_active ? 'School blocked successfully' : 'School unblocked successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update school status');
    }
  };

  // Open Edit School Dialog
  const openEditSchoolDialog = (school) => {
    setSelectedSchool(school);
    setEditFormData({
      name: school.name || '',
      email: school.email || '',
      phone: school.phone || '',
      address: school.address || '',
      current_session: school.current_session || '2026',
      plan_id: school.subscription?.plan_id || '',
      subscription_end_date: school.subscription?.end_date || ''
    });
    setEditSchoolDialog(true);
  };

  // Save School Edit
  const handleSaveSchoolEdit = async () => {
    try {
      setLoading(true);
      
      // Update school info
      await axios.patch(`${API_URL}/api/schools/${selectedSchool.id}`, {
        name: editFormData.name,
        email: editFormData.email,
        phone: editFormData.phone,
        address: editFormData.address,
        current_session: editFormData.current_session
      }, { headers: getAuthHeader() });
      
      // Update subscription ONLY if plan or end_date actually changed
      const originalPlanId = selectedSchool.subscription?.plan_id || '';
      const originalEndDate = selectedSchool.subscription?.end_date || '';
      
      const planChanged = editFormData.plan_id && editFormData.plan_id !== originalPlanId;
      const endDateChanged = editFormData.subscription_end_date && editFormData.subscription_end_date !== originalEndDate;
      
      if (planChanged || endDateChanged) {
        await axios.patch(`${API_URL}/api/subscriptions/school/${selectedSchool.id}/subscription`, {
          plan_id: planChanged ? editFormData.plan_id : undefined,
          end_date: endDateChanged ? editFormData.subscription_end_date : undefined
        }, { headers: getAuthHeader() });
      }
      
      toast.success('School updated successfully');
      setEditSchoolDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update school');
    } finally {
      setLoading(false);
    }
  };

  // Open school detail dialog
  const openSchoolDetail = async (school) => {
    setSelectedSchool(school);
    setSchoolDetailDialog(true);
    
    // Fetch school admins
    await fetchSchoolAdmins(school.id);
  };

  // Quick adjust subscription days (±days) via new dedicated endpoint
  const adjustSubscriptionDays = async (days) => {
    if (!selectedSchool || days === 0) return;
    const action = days > 0 ? `extend by ${days} days` : `reduce by ${Math.abs(days)} days`;
    if (!window.confirm(`Are you sure you want to ${action} for "${selectedSchool.name}"?`)) return;
    try {
      setLoading(true);
      const res = await axios.post(
        `${API_URL}/api/subscriptions/school/${selectedSchool.id}/adjust-days?days=${days}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      // Update local state with new end_date so UI reflects change immediately
      setEditFormData(prev => ({
        ...prev,
        subscription_end_date: res.data.new_end_date,
        custom_days_adjust: ''
      }));
      setSelectedSchool(prev => prev ? {
        ...prev,
        subscription: { ...(prev.subscription || {}), end_date: res.data.new_end_date }
      } : prev);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust days');
    } finally {
      setLoading(false);
    }
  };

  // Fetch school admins
  const fetchSchoolAdmins = async (schoolId) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/users?school_id=${schoolId}&role=school_admin`, {
        headers: getAuthHeader()
      });
      setSchoolAdmins(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch school admins:', error);
      setSchoolAdmins([]);
      return [];
    }
  };

  // Open reset password dialog
  const openResetPasswordDialog = async (school) => {
    setSelectedSchool(school);
    setResetPasswordDialog(true);
    await fetchSchoolAdmins(school.id);
  };

  // Reset school admin password
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    // Get the admin user ID
    const adminUser = schoolAdmins.find(u => u.role === 'school_admin');
    if (!adminUser) {
      toast.error('No admin user found for this school');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/admin-reset-password`, {
        user_id: adminUser.id,
        new_password: newPassword
      }, { headers: getAuthHeader() });
      
      toast.success('Password reset successfully!');
      setResetPasswordDialog(false);
      setNewPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      // Handle if detail is an object or string
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (errorMsg) {
        toast.error('Failed to reset password');
      } else {
        toast.error('Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  // Open WhatsApp with school admin
  const openWhatsApp = (phone, schoolName) => {
    const message = encodeURIComponent(`Hello, this is Cloud Campus Admin. Regarding your school "${schoolName}"...`);
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  // Calculate days remaining for subscription
  const getDaysRemaining = (subscription) => {
    if (!subscription || !subscription.end_date) return null;
    
    const endDate = new Date(subscription.end_date);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const getSubscriptionBadge = (sub) => {
    if (!sub) return <Badge variant="outline" className="text-gray-500">No Plan</Badge>;
    
    const statusConfig = {
      active: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      trial: { variant: 'secondary', icon: Clock, color: 'text-blue-600' },
      expired: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      cancelled: { variant: 'outline', icon: XCircle, color: 'text-gray-500' }
    };
    
    const config = statusConfig[sub.status] || statusConfig.expired;
    const Icon = config.icon;
    const daysLeft = getDaysRemaining(sub);
    
    return (
      <div className="flex items-center gap-2">
        <Badge variant={config.variant} className="flex items-center gap-1">
          <Icon className={`h-3 w-3 ${config.color}`} />
          {sub.status?.toUpperCase()}
        </Badge>
        {sub.plan && <span className="text-xs text-muted-foreground">{sub.plan.name}</span>}
        {daysLeft !== null && (
          <Badge variant="outline" className={`text-xs ${daysLeft <= 7 ? 'text-red-600 border-red-300' : daysLeft <= 30 ? 'text-amber-600 border-amber-300' : 'text-green-600 border-green-300'}`}>
            {daysLeft > 0 ? `${daysLeft} days left` : 'Expired'}
          </Badge>
        )}
      </div>
    );
  };

  const statCards = [
    { title: 'Total Schools', value: stats.schools, icon: Building2, color: 'bg-blue-500' },
    { title: 'Total Students', value: stats.students, icon: Users, color: 'bg-green-500' },
    { title: 'Total Teachers', value: stats.teachers, icon: GraduationCap, color: 'bg-purple-500' },
    { title: 'Active Subscriptions', value: schools.filter(s => s.subscription?.status === 'active').length, icon: CreditCard, color: 'bg-amber-500' },
  ];

  return (
    <DashboardLayout title="Super Admin Dashboard">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                    <div className={`${stat.color} p-2 rounded-lg`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stat.value}</div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Schools List */}
        <Card data-testid="schools-list-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Schools ({schools.length})</CardTitle>
              <CardDescription>Manage tenant schools and subscriptions</CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-school-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add School
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New School (Tenant)</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Logo Upload */}
                  <div className="flex items-center gap-4">
                    <div 
                      className="relative w-20 h-20 rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors overflow-hidden"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center">
                          <Image className="h-6 w-6 mx-auto text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Logo</span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <div className="text-sm text-muted-foreground">
                      <p>Upload school logo</p>
                      <p className="text-xs">PNG, JPG up to 2MB</p>
                    </div>
                  </div>

                  {/* School Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm border-b pb-2">School Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>School Code *</Label>
                        <Input
                          value={formData.school_code}
                          onChange={(e) => setFormData({ ...formData, school_code: e.target.value.toUpperCase() })}
                          placeholder="e.g., GVHS"
                          maxLength={10}
                          required
                          data-testid="school-code-input"
                        />
                        <p className="text-xs text-muted-foreground">URL: /{formData.school_code.toLowerCase() || 'code'}</p>
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>School Name *</Label>
                        <Input
                          value={formData.school_name}
                          onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                          placeholder="Green Valley High School"
                          required
                          data-testid="school-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={formData.school_email}
                          onChange={(e) => setFormData({ ...formData, school_email: e.target.value })}
                          placeholder="info@school.edu"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone *</Label>
                        <Input
                          value={formData.school_phone}
                          onChange={(e) => setFormData({ ...formData, school_phone: e.target.value })}
                          placeholder="+91 98765 43210"
                          required
                        />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>Address *</Label>
                        <Input
                          value={formData.school_address}
                          onChange={(e) => setFormData({ ...formData, school_address: e.target.value })}
                          placeholder="123 Main Street, City"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Subscription Plan */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm border-b pb-2 flex items-center gap-2">
                      <CreditCard className="h-4 w-4" /> Subscription Plan
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Select Plan</Label>
                        <Select value={formData.plan_id || "none"} onValueChange={(v) => setFormData({ ...formData, plan_id: v === "none" ? "" : v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="No plan (Free trial)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No plan (Free trial)</SelectItem>
                            {plans.map(plan => (
                              <SelectItem key={plan.id} value={plan.id}>
                                {plan.name} - ₹{plan.price_monthly}/mo
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Billing Cycle</Label>
                        <Select value={formData.billing_cycle} onValueChange={(v) => setFormData({ ...formData, billing_cycle: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly (10% off)</SelectItem>
                            <SelectItem value="yearly">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Admin Account */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm border-b pb-2">School Admin Account</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Admin Name *</Label>
                        <Input
                          value={formData.admin_name}
                          onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Admin Email *</Label>
                        <Input
                          type="email"
                          value={formData.admin_email}
                          onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                          placeholder="admin@school.edu"
                          required
                        />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>Password *</Label>
                        <Input
                          type="password"
                          value={formData.admin_password}
                          onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                          placeholder="Min 8 characters"
                          required
                          minLength={8}
                        />
                      </div>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={loading}>
                      {loading ? 'Creating...' : 'Create School'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {schools.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No schools created yet</p>
                </div>
              ) : (
                schools.map((school) => (
                  <motion.div
                    key={school.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    data-testid={`school-${school.school_code}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12">
                          {school.logo_url ? (
                            <AvatarImage src={school.logo_url} alt={school.name} />
                          ) : null}
                          <AvatarFallback className="bg-primary/10 text-primary font-bold">
                            {school.school_code?.slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{school.name}</h4>
                            <Badge variant="outline">{school.school_code}</Badge>
                            {school.is_active === false && (
                              <Badge variant="destructive">BLOCKED</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{school.email}</p>
                          <div className="mt-1">
                            {getSubscriptionBadge(school.subscription)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(getSchoolPortalUrl(school.school_code))}
                          title="Copy portal URL"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(getSchoolPortalUrl(school.school_code), '_blank')}
                          title="Open portal"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        
                        {/* More Actions Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openSchoolDetail(school)}>
                              <Eye className="h-4 w-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditSchoolDialog(school)}>
                              <Edit className="h-4 w-4 mr-2" /> Edit School
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openResetPasswordDialog(school)}>
                              <Key className="h-4 w-4 mr-2" /> Reset Password
                            </DropdownMenuItem>
                            {school.phone && (
                              <DropdownMenuItem onClick={() => openWhatsApp(school.phone, school.name)}>
                                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => toggleSchoolStatus(school)}
                              className={school.is_active !== false ? 'text-red-600' : 'text-green-600'}
                            >
                              {school.is_active !== false ? (
                                <><Ban className="h-4 w-4 mr-2" /> Block School</>
                              ) : (
                                <><CheckCircle className="h-4 w-4 mr-2" /> Unblock School</>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    
                    {/* Tenant Login Info Section */}
                    <div className="mt-3 pt-3 border-t border-dashed">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Login URL:</span>
                          <code className="bg-muted px-2 py-0.5 rounded text-xs">
                            /{school.school_code?.toLowerCase()}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(getSchoolPortalUrl(school.school_code))}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Admin ID:</span>
                          <code className="bg-muted px-2 py-0.5 rounded text-xs">
                            {school.admin_info?.email || 'N/A'}
                          </code>
                          {school.admin_info?.email && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => copyToClipboard(school.admin_info.email)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Password:</span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs"
                            onClick={() => openResetPasswordDialog(school)}
                          >
                            <Key className="h-3 w-3 mr-1" /> Reset
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* School Detail Dialog */}
        <Dialog open={schoolDetailDialog} onOpenChange={setSchoolDetailDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {selectedSchool?.name}
              </DialogTitle>
            </DialogHeader>
            {selectedSchool && (
              <div className="space-y-4">
                {/* School Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">School Code</p>
                    <p className="font-semibold">{selectedSchool.school_code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge variant={selectedSchool.is_active !== false ? 'default' : 'destructive'}>
                      {selectedSchool.is_active !== false ? 'Active' : 'Blocked'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedSchool.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{selectedSchool.phone || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium">{selectedSchool.address || 'N/A'}</p>
                  </div>
                </div>

                {/* Subscription Info */}
                <div className="p-4 border rounded-lg">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" /> Subscription Details
                  </h4>
                  {selectedSchool.subscription ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Plan</p>
                        <p className="font-medium">{selectedSchool.subscription.plan?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge variant={selectedSchool.subscription.status === 'active' ? 'default' : 'secondary'}>
                          {selectedSchool.subscription.status}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Start Date</p>
                        <p className="font-medium">{selectedSchool.subscription.start_date}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">End Date</p>
                        <p className="font-medium">{selectedSchool.subscription.end_date}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-muted-foreground">Days Remaining</p>
                        <p className={`font-bold text-lg ${getDaysRemaining(selectedSchool.subscription) <= 7 ? 'text-red-600' : getDaysRemaining(selectedSchool.subscription) <= 30 ? 'text-amber-600' : 'text-green-600'}`}>
                          {getDaysRemaining(selectedSchool.subscription)} days
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No subscription assigned</p>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  {selectedSchool.phone && (
                    <Button variant="outline" onClick={() => openWhatsApp(selectedSchool.phone, selectedSchool.name)}>
                      <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => { setSchoolDetailDialog(false); setResetPasswordDialog(true); }}>
                    <Key className="h-4 w-4 mr-2" /> Reset Password
                  </Button>
                  <Button 
                    variant={selectedSchool.is_active !== false ? 'destructive' : 'default'}
                    onClick={() => { toggleSchoolStatus(selectedSchool); setSchoolDetailDialog(false); }}
                  >
                    {selectedSchool.is_active !== false ? (
                      <><Ban className="h-4 w-4 mr-2" /> Block</>
                    ) : (
                      <><CheckCircle className="h-4 w-4 mr-2" /> Unblock</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialog} onOpenChange={setResetPasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset School Admin Password</DialogTitle>
            </DialogHeader>
            {selectedSchool && (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">School</p>
                  <p className="font-semibold">{selectedSchool.name} ({selectedSchool.school_code})</p>
                </div>
                
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 8 chars)"
                    />
                    <Button type="button" variant="outline" onClick={generatePassword}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  {newPassword && (
                    <p className="text-sm text-muted-foreground">
                      Generated: <code className="bg-muted px-2 py-1 rounded">{newPassword}</code>
                    </p>
                  )}
                </div>

                <div className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
                  <p className="font-medium">After resetting:</p>
                  <p>• Copy the password and share via WhatsApp</p>
                  <p>• Admin email: {selectedSchool.email}</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setResetPasswordDialog(false); setNewPassword(''); setSchoolAdmins([]); }}>
                Cancel
              </Button>
              <Button onClick={() => handleResetPassword()} disabled={loading || !newPassword || schoolAdmins.length === 0}>
                {loading ? 'Resetting...' : schoolAdmins.length === 0 ? 'Loading...' : 'Reset Password'}
              </Button>
              {newPassword && selectedSchool?.phone && (
                <Button variant="secondary" onClick={() => {
                  const message = encodeURIComponent(`Your Cloud Campus login details:\nSchool: ${selectedSchool.name}\nEmail: ${selectedSchool.email}\nNew Password: ${newPassword}`);
                  const cleanPhone = selectedSchool.phone.replace(/[^0-9]/g, '');
                  window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
                }}>
                  <MessageCircle className="h-4 w-4 mr-2" /> Share via WhatsApp
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit School Dialog */}
        <Dialog open={editSchoolDialog} onOpenChange={setEditSchoolDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit School: {selectedSchool?.name}
              </DialogTitle>
            </DialogHeader>
            {selectedSchool && (
              <div className="space-y-4">
                {/* School Info Section */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>School Name</Label>
                    <Input
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current Session</Label>
                    <Input
                      value={editFormData.current_session}
                      onChange={(e) => setEditFormData({...editFormData, current_session: e.target.value})}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={editFormData.address}
                      onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                    />
                  </div>
                </div>

                {/* Subscription Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" /> Subscription Settings
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Plan</Label>
                      <Select
                        value={editFormData.plan_id}
                        onValueChange={(value) => setEditFormData({...editFormData, plan_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {plans.map(plan => (
                            <SelectItem key={plan.id} value={plan.id}>
                              {plan.name} - ₹{plan.price_monthly}/month
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Subscription End Date</Label>
                      <Input
                        type="date"
                        value={editFormData.subscription_end_date}
                        onChange={(e) => setEditFormData({...editFormData, subscription_end_date: e.target.value})}
                        data-testid="edit-subscription-end-date"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Current: {selectedSchool.subscription?.plan_id || 'No Plan'} | 
                    Expires: {selectedSchool.subscription?.end_date || 'N/A'}
                  </p>

                  {/* Quick +/- days buttons */}
                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
                    <Label className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                      Quick Adjust Days
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Instantly extend or reduce this school&apos;s subscription. No form re-fill needed.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[+7, +15, +30, +60, +90, +365].map(d => (
                        <Button key={`plus-${d}`} size="sm" variant="outline"
                          className="border-green-600 text-green-700 hover:bg-green-50"
                          onClick={() => adjustSubscriptionDays(d)} disabled={loading}
                          data-testid={`extend-days-${d}`}>
                          +{d}d
                        </Button>
                      ))}
                      {[-7, -15, -30].map(d => (
                        <Button key={`minus-${d}`} size="sm" variant="outline"
                          className="border-red-600 text-red-700 hover:bg-red-50"
                          onClick={() => adjustSubscriptionDays(d)} disabled={loading}
                          data-testid={`reduce-days-${d}`}>
                          {d}d
                        </Button>
                      ))}
                      <div className="flex items-center gap-1 ml-2">
                        <Input
                          type="number"
                          placeholder="±custom"
                          value={editFormData.custom_days_adjust || ''}
                          onChange={(e) => setEditFormData({...editFormData, custom_days_adjust: e.target.value})}
                          className="h-8 w-24 text-xs"
                          data-testid="custom-days-input"
                        />
                        <Button size="sm" variant="secondary"
                          onClick={() => {
                            const val = parseInt(editFormData.custom_days_adjust || '0', 10);
                            if (val !== 0 && !isNaN(val)) adjustSubscriptionDays(val);
                          }}
                          disabled={loading || !editFormData.custom_days_adjust}
                          data-testid="apply-custom-days">
                          Apply
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditSchoolDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSchoolEdit} disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tenant Reset Manager */}
        <TenantResetManager schools={schools} />
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminDashboard;
